/**
 * @fileoverview Hook for loading state and county lookup values used by the
 *   department details slide-in's State/County selectors. Uses the SAME
 *   `listLookups` function the geo cache upload system and the onboarding
 *   wizard use, so jurisdiction values stay consistent with the geo cache
 *   catalog's FIPS keys.
 * @module system-web/hooks/useJurisdictionLookups
 *
 * Responsibilities:
 * - Load all active state lookup values via `listLookups({ lookupType: 'state' })`.
 * - Load counties for a given state via
 *   `listLookups({ lookupType: 'county', parentCode: stateCode })`.
 * - Expose a stable `getCountyItem` helper so callers can resolve the raw
 *   county document (which carries `config.fipsCode`) for FIPS derivation.
 *
 * Dependencies:
 * - ../services/lookupService (listLookups, LookupValue — the shared lookup
 *   package function used across the system, including geo cache upload).
 *
 * Architecture:
 * - Local-first. Reads from the system Dexie `system_lookups` table through
 *   `listLookups`; no direct API calls.
 * - Loads are triggered in mount/state-change effects, never inside a
 *   liveQuery observed function.
 *
 * Notes:
 * - States are loaded without a `parentCode` (all states) to match the geo
 *   cache upload modal's behavior and avoid requiring a country to be set.
 * - Counties cascade from the selected state code.
 *
 * @localFirst
 * Source of Truth: `system_lookups` Dexie table (local cache, system-api sync).
 * Write Flow: read-only hook; writes go through lookupService save functions.
 * Sync Strategy: lookup values are synced to Dexie by the standard sync pipeline.
 * Conflict Resolution: n/a (server-authoritative lookups).
 *
 * @hook useJurisdictionLookups
 *
 * Purpose:
 * - Provide cascading state/county lookup values for the department details
 *   slide-in's jurisdiction selectors.
 *
 * Returns:
 * - states:        Live list of state LookupValue rows.
 * - counties:      Live list of county LookupValue rows for the selected state.
 * - loadingStates: True while the state list is loading.
 * - loadingCounties: True while the county list is loading.
 * - getCountyItem: Resolve a raw county lookup row by code (for FIPS derivation).
 *
 * Side Effects:
 * - On mount: loads all states.
 * - On stateCode change: loads counties for that state.
 *
 * Offline Behavior:
 * - Lookup values from the last successful sync are available offline.
 */
import { useState, useEffect, useCallback } from 'react';
import { listLookups, type LookupValue } from '../services/lookupService';

export interface UseJurisdictionLookupsResult {
  countries: LookupValue[];
  states: LookupValue[];
  counties: LookupValue[];
  loadingCountries: boolean;
  loadingStates: boolean;
  loadingCounties: boolean;
  getCountyItem: (code: string) => LookupValue | undefined;
}

function dedupeByCode(values: LookupValue[]): LookupValue[] {
  const seen = new Set<string>();
  return values.filter((item) => {
    if (seen.has(item.code)) return false;
    seen.add(item.code);
    return true;
  });
}

export function useJurisdictionLookups(stateCode?: string): UseJurisdictionLookupsResult {
  const [countries, setCountries] = useState<LookupValue[]>([]);
  const [states, setStates] = useState<LookupValue[]>([]);
  const [counties, setCounties] = useState<LookupValue[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCounties, setLoadingCounties] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingCountries(true);
    listLookups({ lookupType: 'country' })
      .then((data) => {
        if (!cancelled) setCountries(dedupeByCode(data));
      })
      .catch(() => {
        if (!cancelled) setCountries([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCountries(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load all states on mount. No parentCode — matches the geo cache upload
  // modal so a department can pick a state even before a country is set.
  useEffect(() => {
    let cancelled = false;
    setLoadingStates(true);
    listLookups({ lookupType: 'state' })
      .then((data) => {
        if (!cancelled) setStates(dedupeByCode(data));
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStates(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load counties for the selected state. County lookups carry the full
  // 5-digit state+county FIPS in `code` / `config.fipsCode`.
  useEffect(() => {
    if (!stateCode) {
      setCounties([]);
      return;
    }
    let cancelled = false;
    setLoadingCounties(true);
    listLookups({ lookupType: 'county', parentCode: stateCode })
      .then((data) => {
        if (!cancelled) setCounties(dedupeByCode(data));
      })
      .catch(() => {
        if (!cancelled) setCounties([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCounties(false);
      });
    return () => {
      cancelled = true;
    };
  }, [stateCode]);

  const getCountyItem = useCallback(
    (code: string): LookupValue | undefined => counties.find((c) => c.code === code),
    [counties],
  );

  return { countries, states, counties, loadingCountries, loadingStates, loadingCounties, getCountyItem };
}
